import { useConfig } from '../context/ConfigContext';
import { formatMoney, formatAmount, getCurrencySymbol, roundMoney } from '../utils/currencyUtil';
import '../css/Pos.css';

/**
 * Turns a typed money field into a number, or null when it is blank or not a
 * usable amount — so "not entered yet" never reads as 0.
 *
 * @param {number|string} value
 * @returns {number|null}
 */
const parseMoneyField = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

/**
 * The product form's Pricing section: what one SELLS for, what one COSTS the
 * club to make or buy (the "puhunan"), and — once both are filled in — what
 * that leaves per item.
 *
 * Its own section rather than squeezed beside the stock count: the two prices
 * are read together, and the profit line under them only makes sense with
 * both in view.
 *
 * COST FIRST, then the selling price: the price is set from what the item
 * costs, so the fields are read in that order.
 *
 * The profit line is worked out here for the person setting the price, and is
 * never stored — it would be out of date the moment either price changed.
 *
 * @param {object} props
 * @param {number|string} props.price - the selling price, as typed
 * @param {(value: string) => void} props.onPriceChange
 * @param {number|string} props.costPrice - the cost price, as typed; '' when not set
 * @param {(value: string) => void} props.onCostPriceChange
 * @param {boolean} [props.disabled] - while the product form is saving
 * @returns {JSX.Element}
 */
function PosPricingSection({ price, onPriceChange, costPrice, onCostPriceChange, disabled = false }) {
  const { config } = useConfig();
  const symbol = getCurrencySymbol(config);

  const sell = parseMoneyField(price);
  const cost = parseMoneyField(costPrice);
  const profit = sell !== null && cost !== null ? roundMoney(sell - cost) : null;
  // Margin is profit as a share of the SELLING price — the usual way a shop
  // reads it. Undefined at a price of 0, so it is simply left off there.
  const margin = profit !== null && sell > 0 ? roundMoney((profit / sell) * 100) : null;

  return (
    <section className="pos-form-section" aria-labelledby="pos-pricing-title">
      <p id="pos-pricing-title" className="pos-opt-section">Pricing</p>

      <div className="pos-field-row">
        <label className="pos-field">
          <span>Cost price ({symbol})</span>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={costPrice}
            onChange={(e) => onCostPriceChange(e.target.value)}
            disabled={disabled}
            placeholder="Optional"
          />
          <span className="pos-theme-hint">What one costs you to make or buy (puhunan).</span>
        </label>

        <label className="pos-field">
          <span>Selling price ({symbol})</span>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={price}
            onChange={(e) => onPriceChange(e.target.value)}
            disabled={disabled}
          />
          <span className="pos-theme-hint">What a customer pays for one.</span>
        </label>
      </div>

      {profit !== null && (
        <p className={`pos-pricing-profit${profit < 0 ? ' loss' : ''}`}>
          {profit < 0 ? 'Loss per item: ' : 'Profit per item: '}
          <strong>{formatMoney(Math.abs(profit), config)}</strong>
          {margin !== null && ` (${formatAmount(margin)}% margin)`}
          {profit < 0 && ' — the selling price is below the cost.'}
        </p>
      )}
    </section>
  );
}

export default PosPricingSection;
